import React from 'react';
import { Handle, Position } from 'reactflow';
import { Variable } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui';

type VariableOperation = 'set' | 'get' | 'transform' | 'random_choice' | 'set_literal';

interface VariableNodeData {
  label?: string;
  variable?: {
    name?: string;
    operation?: VariableOperation;
    value?: unknown;
    type?: string;
  };
  /** Legacy flat shape written by the editor before #203 */
  variableName?: string;
  operation?: VariableOperation;
  value?: string;
}

export default function VariableNode({
  data,
  selected
}: {
  data: VariableNodeData;
  selected?: boolean
}) {
  // Canonical shape is nested data.variable.* (what the executor reads);
  // fall back to the legacy flat fields for flows saved before #203
  const name = data.variable?.name ?? data.variableName;
  const operation = data.variable?.operation ?? data.operation;
  const rawValue = data.variable?.value ?? data.value;
  // For random_choice the value is a list — show "N options: first, second, …"
  // instead of the raw JSON so the node reads clearly on the canvas.
  const asList = Array.isArray(rawValue)
    ? (rawValue as unknown[])
    : operation === 'random_choice' && typeof rawValue === 'string'
      ? rawValue.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
      : null;
  const displayValue =
    operation === 'random_choice' && asList
      ? `${asList.length} option${asList.length === 1 ? '' : 's'}: ${asList.slice(0, 3).join(', ')}${asList.length > 3 ? ', …' : ''}`
      : rawValue === undefined || rawValue === null || rawValue === ''
        ? undefined
        : typeof rawValue === 'object'
          ? JSON.stringify(rawValue)
          : String(rawValue);

  const operationLabel =
    operation === 'random_choice' ? 'random' : operation === 'set_literal' ? 'literal' : operation;

  const getOperationColor = () => {
    switch (operation) {
      case 'set': return 'secondary';
      case 'get': return 'secondary';
      case 'transform': return 'outline';
      case 'random_choice': return 'outline';
      case 'set_literal': return 'outline';
      default: return 'secondary';
    }
  };

  const getOperationClassName = () => {
    switch (operation) {
      case 'set': return '';
      case 'get': return 'bg-status-success text-white';
      case 'transform': return 'border-status-warning text-status-warning';
      case 'random_choice': return 'border-category-data text-category-data';
      case 'set_literal': return 'border-muted-foreground text-muted-foreground';
      default: return '';
    }
  };

  return (
    <div className={cn(
      "min-w-[160px] rounded-lg",
      "bg-card",
      "border-2 transition-all duration-fast",
      selected
        ? "border-category-data shadow-glow-focus"
        : "border-category-data/50 dark:border-category-data/30 shadow-md hover:shadow-lg"
    )}>
      {/* Connection Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-category-data !border-category-data"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-category-data-bg flex items-center justify-center">
          <Variable className="w-3.5 h-3.5 text-category-data" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Variable
          </div>
          <div className="text-xs text-muted-foreground/70 font-mono truncate">
            ${name || 'unnamed'}
          </div>
        </div>
        {operation && (
          <Badge variant={getOperationColor() as any} className={getOperationClassName()}>
            {operationLabel}
          </Badge>
        )}
      </div>

      {/* Node Body */}
      {displayValue && (
        <div className="p-3">
          <div className="text-xs font-mono bg-muted p-2 rounded border border-border dark:border-border-subtle">
            <span className="text-muted-foreground">{operationLabel || 'set'}: </span>
            <span className="text-foreground">{displayValue}</span>
          </div>
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !bg-category-data !border-category-data"
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}

